<template>
  <div class="travel-advice-wrap" v-if="shioriData">
    <!-- ヘッダー -->
    <div class="advice-header">
      <span class="advice-header-icon">💡</span>
      <h3 class="advice-header-title">旅行アドバイス</h3>
      <span class="advice-ai-badge">自動提案</span>
    </div>

    <!-- カウントダウン -->
    <div
      v-if="countdownInfo"
      class="countdown-banner"
      :class="countdownInfo.cls"
    >
      <span class="countdown-icon">{{ countdownInfo.icon }}</span>
      <div class="countdown-text">
        <div class="countdown-main">{{ countdownInfo.main }}</div>
        <div v-if="countdownInfo.sub" class="countdown-sub">
          {{ countdownInfo.sub }}
        </div>
      </div>
    </div>

    <!-- アドバイスセクション -->
    <div class="advice-body">
      <div
        v-for="section in adviceSections"
        :key="section.title"
        class="advice-section"
      >
        <div class="advice-section-title">
          <span>{{ section.icon }}</span>
          <strong>{{ section.title }}</strong>
        </div>
        <ul class="advice-list">
          <li
            v-for="(item, i) in section.items"
            :key="i"
            class="advice-item"
            :class="`level-${item.level}`"
          >
            <span class="advice-dot"></span>
            <span>{{ item.text }}</span>
          </li>
        </ul>
      </div>

      <!-- 旅行先未設定の場合 -->
      <div v-if="adviceSections.length === 0" class="advice-empty">
        しおりに旅行先を登録すると、気候・ビザ・持ち物などの詳細なアドバイスを確認できます。
      </div>
    </div>

    <p class="advice-disclaimer">
      ※ 自動生成された参考情報です。最新情報は各公式機関でご確認ください。
    </p>

    <div v-if="officialSourceLinks.length > 0" class="official-links">
      <span class="official-links-label">公式情報</span>
      <a
        v-for="link in officialSourceLinks"
        :key="link.href"
        :href="link.href"
        class="official-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ link.label }}
      </a>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { computed, ref, watch } from "vue";
import { API_URL } from "@/config/api.js";

// ---- ヘルパー ----
function toDateStr(d) {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function daysDiff(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = toDateStr(dateStr);
  return Math.round((target - today) / 86400000);
}

function getDestStr(destinations) {
  return Array.isArray(destinations) ? destinations.join(" ") : "";
}

const CURRENCY_INFO_RULES = [
  {
    pattern: /韓国|ソウル|釜山|済州/,
    code: "KRW",
    name: "ウォン",
    note: "到着後両替またはATMが便利",
  },
  { pattern: /台湾|台北|高雄/, code: "TWD", name: "台湾ドル" },
  {
    pattern: /中国|北京|上海|深圳/,
    code: "CNY",
    name: "人民元",
    note: "アリペイ等のQR決済も普及",
  },
  { pattern: /香港/, code: "HKD", name: "香港ドル" },
  { pattern: /マカオ/, code: "MOP", name: "パタカ", note: "香港ドルも通用" },
  { pattern: /シンガポール/, code: "SGD", name: "シンガポールドル" },
  { pattern: /タイ|バンコク/, code: "THB", name: "タイバーツ" },
  {
    pattern: /ベトナム|ハノイ|ホーチミン/,
    code: "VND",
    name: "ベトナムドン",
    note: "現地ATMでの引き出しが便利",
  },
  {
    pattern: /マレーシア|クアラルンプール/,
    code: "MYR",
    name: "マレーシアリンギット",
  },
  {
    pattern: /インドネシア|バリ|ジャカルタ/,
    code: "IDR",
    name: "インドネシアルピア",
  },
  {
    pattern: /フィリピン|マニラ|セブ/,
    code: "PHP",
    name: "フィリピンペソ",
  },
  {
    pattern: /インド(?!ネシア)|デリー|ムンバイ/,
    code: "INR",
    name: "インドルピー",
  },
  {
    pattern: /UAE|アラブ首長国連邦|ドバイ|アブダビ/,
    code: "AED",
    name: "UAEディルハム",
  },
  {
    pattern: /サウジアラビア|リヤド/,
    code: "SAR",
    name: "サウジアラビアリヤル",
  },
  { pattern: /トルコ|イスタンブール/, code: "TRY", name: "トルコリラ" },
  {
    pattern: /ハワイ|グアム|サイパン|アメリカ|ロサンゼルス|ニューヨーク/,
    code: "USD",
    name: "米ドル",
  },
  { pattern: /カナダ/, code: "CAD", name: "カナダドル" },
  {
    pattern:
      /フランス|ドイツ|イタリア|スペイン|オランダ|ポルトガル|オーストリア|ベルギー/,
    code: "EUR",
    name: "ユーロ",
  },
  { pattern: /イギリス|ロンドン/, code: "GBP", name: "ポンド" },
  { pattern: /スイス/, code: "CHF", name: "スイスフラン" },
  { pattern: /オーストラリア/, code: "AUD", name: "オーストラリアドル" },
  { pattern: /ニュージーランド/, code: "NZD", name: "ニュージーランドドル" },
  {
    pattern: /スウェーデン|ストックホルム/,
    code: "SEK",
    name: "スウェーデンクローナ",
  },
  { pattern: /ノルウェー|オスロ/, code: "NOK", name: "ノルウェークローネ" },
  {
    pattern: /デンマーク|コペンハーゲン/,
    code: "DKK",
    name: "デンマーククローネ",
  },
  { pattern: /ポーランド|ワルシャワ/, code: "PLN", name: "ズウォティ" },
  { pattern: /チェコ|プラハ/, code: "CZK", name: "チェココルナ" },
  {
    pattern: /メキシコ|メキシコシティ/,
    code: "MXN",
    name: "メキシコペソ",
  },
  {
    pattern: /ブラジル|リオデジャネイロ|サンパウロ/,
    code: "BRL",
    name: "ブラジルレアル",
  },
];

function getCurrencyInfos(destinations) {
  const currencies = new Map();
  (Array.isArray(destinations) ? destinations : []).forEach((destination) => {
    const destinationText = String(destination || "");
    CURRENCY_INFO_RULES.forEach((entry) => {
      if (entry.pattern.test(destinationText) && !currencies.has(entry.code)) {
        currencies.set(entry.code, entry);
      }
    });
  });
  return [...currencies.values()];
}

function formatCurrencyAmount(amount, maximumFractionDigits = 2) {
  return Number(amount).toLocaleString("ja-JP", {
    maximumFractionDigits,
  });
}

function formatRateDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 10);
}

function formatExchangeRateItem(currencyInfo, exchangeRateInfo) {
  if (!currencyInfo) return null;
  if (exchangeRateInfo.loading) {
    return { text: "為替レート: 最新レートを取得中...", level: "info" };
  }
  if (exchangeRateInfo.error || exchangeRateInfo.missingCodes.includes(currencyInfo.code)) {
    return {
      text: `為替レート: ${currencyInfo.code}は取得できませんでした。両替前に最新レートを確認してください`,
      level: "warn",
    };
  }
  const rate = exchangeRateInfo.rates[currencyInfo.code];
  if (!rate || rate <= 0) return null;

  const yenPerUnit = 1 / rate;
  const foreignPerTenThousandYen = 10000 * rate;
  const rateDate = formatRateDate(exchangeRateInfo.date);
  const dateText = rateDate ? `（${rateDate}時点）` : "";
  return {
    text: `為替レート: 1 ${currencyInfo.code} ≒ ¥${formatCurrencyAmount(
      yenPerUnit,
      2,
    )} / ¥10,000 ≒ ${formatCurrencyAmount(
      foreignPerTenThousandYen,
      0,
    )} ${currencyInfo.code}${dateText}`,
    level: "info",
  };
}

// ---- 渡航先アドバイスルール ----
function getTemperatureItem(destType, destStr, month) {
  const season =
    month <= 2 || month === 12
      ? "winter"
      : month <= 5
      ? "spring"
      : month <= 8
      ? "summer"
      : "autumn";

  if (destType === "domestic") {
    const domesticMap = {
      spring: "目安気温: 10〜22℃前後。日中と朝晩の寒暖差に備えて薄手の羽織りを",
      summer: "目安気温: 25〜35℃前後。熱中症対策と通気性のよい服装を",
      autumn: "目安気温: 12〜24℃前後。朝晩は冷えるため重ね着しやすい服装を",
      winter: "目安気温: 0〜12℃前後。防寒具と歩きやすい靴を",
    };

    if (/北海道/.test(destStr)) {
      return {
        text:
          season === "winter"
            ? "目安気温: -5〜5℃前後。厚手の防寒具・滑りにくい靴を"
            : "目安気温は本州より低め。朝晩用の羽織りを追加で準備",
        level: season === "winter" ? "warn" : "info",
      };
    }

    if (/沖縄|宮古|石垣/.test(destStr)) {
      return {
        text:
          season === "winter"
            ? "目安気温: 15〜22℃前後。薄手の上着があると安心"
            : "目安気温: 24〜32℃前後。日差し・湿度・急な雨への対策を",
        level: "info",
      };
    }

    return {
      text: domesticMap[season],
      level: season === "summer" ? "warn" : "info",
    };
  }

  const tropical =
    /ハワイ|グアム|サイパン|バリ|プーケット|シンガポール|パタヤ|ダナン|マニラ|セブ|モルディブ|ボラボラ/.test(
      destStr,
    );
  if (tropical) {
    return {
      text: "目安気温: 24〜32℃前後。夏服中心で、冷房対策の薄手の羽織りも",
      level: "info",
    };
  }

  if (/オーストラリア|ニュージーランド/.test(destStr)) {
    const southern =
      season === "winter"
        ? "夏"
        : season === "spring"
        ? "秋"
        : season === "summer"
        ? "冬"
        : "春";
    return {
      text: `南半球のため季節は日本と逆で現地は${southern}目安。都市別の気温を出発前に確認`,
      level: "info",
    };
  }

  if (
    /フランス|パリ|ドイツ|ベルリン|イタリア|ローマ|スペイン|マドリード|オランダ|スイス|イギリス|ロンドン/.test(
      destStr,
    )
  ) {
    const europeMap = {
      spring: "目安気温: 8〜20℃前後。薄手コートや羽織りで調整を",
      summer: "目安気温: 18〜30℃前後。日差し対策と歩きやすい靴を",
      autumn: "目安気温: 8〜18℃前後。雨具と重ね着を準備",
      winter: "目安気温: 0〜8℃前後。防寒具と雨・雪対策を",
    };
    return {
      text: europeMap[season],
      level: season === "winter" ? "warn" : "info",
    };
  }

  if (/韓国|ソウル|釜山|台湾|台北|中国|北京|上海|香港/.test(destStr)) {
    return {
      text: "目安気温は日本の同季節に近め。渡航都市の週間予報で最終調整を",
      level: "info",
    };
  }

  return {
    text: "渡航時期の平均気温と週間予報を確認し、重ね着できる服装で調整",
    level: "info",
  };
}

function getVisaItems(destStr, days) {
  const items = [];
  const urgency = days < 30 ? "warn" : "info";

  items.push({
    text: "パスポートの有効期限を確認（帰国後6ヶ月以上の残存期限が必要な国もあり）",
    level: urgency,
  });

  if (
    /アメリカ|ハワイ|グアム|サイパン|ロサンゼルス|ニューヨーク/.test(destStr)
  ) {
    items.push({
      text: "ESTA（電子渡航認証）の申請が必要 — 出発72時間前までに申請を",
      level: days < 14 ? "danger" : "warn",
    });
  } else if (/カナダ/.test(destStr)) {
    items.push({
      text: "eTA（電子渡航認証）の申請が必要",
      level: days < 14 ? "danger" : "warn",
    });
  } else if (/オーストラリア/.test(destStr)) {
    items.push({
      text: "ETA（電子渡航認証）の申請が必要",
      level: days < 14 ? "danger" : "warn",
    });
  } else if (/ニュージーランド/.test(destStr)) {
    items.push({
      text: "NZeTA（電子渡航認証）の申請が必要",
      level: days < 14 ? "danger" : "warn",
    });
  } else if (/中国|北京|上海|深圳|広州/.test(destStr)) {
    items.push({
      text: "ビザの事前申請が必要（中国ビザ申請センター）— 数週間かかる場合あり",
      level: "danger",
    });
  } else if (/インド|ムンバイ|デリー/.test(destStr)) {
    items.push({
      text: "e-Visa または大使館でのビザ申請が必要",
      level: "warn",
    });
  } else if (/韓国|ソウル|釜山|済州/.test(destStr)) {
    items.push({
      text: "ビザ不要（90日以内）。K-ETAの事前申請を推奨",
      level: "ok",
    });
  } else if (/台湾|台北|高雄|台中/.test(destStr)) {
    items.push({ text: "ビザ不要（90日以内）", level: "ok" });
  } else if (/タイ|バンコク|チェンマイ|プーケット|パタヤ/.test(destStr)) {
    items.push({ text: "ビザ免除（60日以内）", level: "ok" });
  } else if (/ベトナム|ハノイ|ホーチミン|ダナン/.test(destStr)) {
    items.push({ text: "ビザ免除（45日以内）またはe-Visa申請可", level: "ok" });
  } else if (
    /フランス|パリ|ドイツ|ベルリン|イタリア|ローマ|スペイン|マドリード|オランダ|スイス|ポルトガル|ギリシャ|オーストリア|ベルギー/.test(
      destStr,
    )
  ) {
    items.push({
      text: "シェンゲン協定国 — ビザ不要（90日以内）",
      level: "ok",
    });
  } else if (/イギリス|ロンドン/.test(destStr)) {
    items.push({ text: "イギリス入国はビザ不要（6ヶ月以内）", level: "ok" });
  } else if (/香港|マカオ/.test(destStr)) {
    items.push({ text: "ビザ不要（90日以内）", level: "ok" });
  } else if (/シンガポール/.test(destStr)) {
    items.push({ text: "ビザ不要（90日以内）", level: "ok" });
  } else if (/マレーシア|クアラルンプール/.test(destStr)) {
    items.push({ text: "ビザ不要（90日以内）", level: "ok" });
  } else {
    items.push({
      text: "旅行先のビザ要件を外務省サイトで必ず確認してください",
      level: "warn",
    });
  }

  return items;
}

function getClimateItems(destType, destStr, month) {
  const items = [getTemperatureItem(destType, destStr, month)];
  const isTropical =
    /ハワイ|グアム|サイパン|バリ|プーケット|シンガポール|パタヤ|ダナン|マニラ|セブ|モルディブ|ボラボラ/.test(
      destStr,
    );

  if (destType === "international" && isTropical) {
    items.push({
      text: "熱帯・亜熱帯気候 — 夏服中心、日焼け止め・帽子・サングラス必携",
      level: "info",
    });
    items.push({
      text: "室内冷房が強い場合があります。薄手の羽織りを1枚持参推奨",
      level: "info",
    });
    items.push({
      text: "スコール（急な雨）があるため折りたたみ傘が便利",
      level: "info",
    });
    return items;
  }

  const season =
    month <= 2 || month === 12
      ? "winter"
      : month <= 5
      ? "spring"
      : month <= 8
      ? "summer"
      : "autumn";

  const seasonMap = {
    spring: {
      text: "春（3〜5月）— 気温差が大きい。重ね着を意識した服装で",
      level: "info",
    },
    summer: {
      text: "夏（6〜8月）— 高温・多湿、熱中症対策（水分・日焼け止め）が重要",
      level: "warn",
    },
    autumn: {
      text: "秋（9〜11月）— 過ごしやすい気候。朝晩の冷え込みに注意",
      level: "info",
    },
    winter: {
      text: "冬（12〜2月）— 防寒具必携。ヒートテック・厚手コートを用意して",
      level: "info",
    },
  };
  items.push(seasonMap[season]);

  if (/北海道/.test(destStr) && season === "summer") {
    items.push({
      text: "北海道の夏は涼しく過ごしやすい。軽い上着があると安心",
      level: "ok",
    });
  }
  if (/沖縄|宮古|石垣/.test(destStr) && season === "winter") {
    items.push({
      text: "沖縄の冬は温暖（15〜20℃）。薄手の上着程度で十分",
      level: "ok",
    });
  }
  if (/ヨーロッパ|フランス|イタリア|スペイン/.test(destStr)) {
    items.push({
      text: "ヨーロッパは石畳が多く歩く機会が増えます。歩きやすい靴を",
      level: "info",
    });
  }

  return items;
}

function getSafetyItems(destType, destStr) {
  if (destType !== "international") return [];

  const items = [
    {
      text: "外務省「海外安全ホームページ」で危険情報・スポット情報・広域情報を確認",
      level: "warn",
    },
    {
      text: "たびレジ登録、緊急連絡先、在外公館の連絡先を控えておく",
      level: "info",
    },
  ];

  if (
    /フランス|パリ|イタリア|ローマ|スペイン|マドリード|イギリス|ロンドン|ヨーロッパ/.test(
      destStr,
    )
  ) {
    items.push({
      text: "観光地・駅・公共交通ではスリや置き引きに注意。バッグは体の前へ",
      level: "warn",
    });
  } else if (
    /タイ|バンコク|ベトナム|ハノイ|ホーチミン|フィリピン|マニラ|セブ|インドネシア|バリ/.test(
      destStr,
    )
  ) {
    items.push({
      text: "夜間移動、流しのタクシー、交通事故に注意。配車アプリ利用を推奨",
      level: "warn",
    });
  } else if (
    /アメリカ|ロサンゼルス|ニューヨーク|ハワイ|グアム/.test(destStr)
  ) {
    items.push({
      text: "地域により治安差があります。夜間の徒歩移動や荷物の車内放置は避けて",
      level: "warn",
    });
  }

  return items;
}

function getHealthItems(destType, destStr) {
  if (destType !== "international") return [];

  const items = [
    {
      text: "厚生労働省検疫所 FORTH で感染症・予防接種情報を確認",
      level: "warn",
    },
    {
      text: "海外旅行保険、常備薬、英文の薬剤説明を必要に応じて準備",
      level: "info",
    },
  ];

  if (
    /タイ|ベトナム|フィリピン|インドネシア|バリ|マレーシア|シンガポール|インド|カンボジア|ラオス/.test(
      destStr,
    )
  ) {
    items.push({
      text: "蚊が媒介する感染症に注意。虫よけ、長袖、飲食物の衛生対策を",
      level: "warn",
    });
  }

  if (/インド|ベトナム|タイ|フィリピン|インドネシア|カンボジア/.test(destStr)) {
    items.push({
      text: "水道水や氷、生ものは慎重に。胃腸薬・経口補水液があると安心",
      level: "info",
    });
  }

  return items;
}

function getPackingItems(destType, destStr, duration, days, season) {
  const items = [];

  if (destType === "international") {
    items.push({
      text: "パスポート（コピーも1部持参）",
      level: days < 14 ? "warn" : "info",
    });
    items.push({
      text: "海外旅行保険への加入（スマホアプリ型も便利）",
      level: "info",
    });
    items.push({
      text: "現地通貨の準備、または国際対応クレジットカード",
      level: "info",
    });

    if (
      /アメリカ|カナダ|ヨーロッパ|フランス|イギリス|ドイツ|イタリア|スペイン|オーストラリア/.test(
        destStr,
      )
    ) {
      items.push({
        text: "電源変換アダプター（日本はAタイプ、現地に合わせて準備）",
        level: "info",
      });
    }

    if (duration >= 3) {
      items.push({
        text: "海外用Wi-Fiルーター / 現地SIMカードの事前手配",
        level: "info",
      });
    }
  } else {
    items.push({
      text: "身分証明書（運転免許証・マイナンバーカード）",
      level: "info",
    });
    items.push({ text: "充電器・モバイルバッテリー", level: "info" });
  }

  if (duration >= 5) {
    items.push({
      text: `${duration}日間の旅行 — コインランドリーの場所確認、または十分な着替えを`,
      level: "info",
    });
  }

  if (season === "summer") {
    items.push({ text: "日焼け止め・冷却グッズ・経口補水液", level: "info" });
  }
  if (season === "winter") {
    items.push({ text: "風邪薬・ホッカイロなど防寒グッズ", level: "info" });
  }

  return items;
}

function getOtherCautionItems(destType, destStr) {
  const items = [];

  if (destType === "international") {
    items.push({
      text: "航空券、ホテル、現地交通、入国条件の名前・日付の表記ゆれを確認",
      level: "info",
    });
    items.push({
      text: "スマホ紛失に備えて、予約情報と身分証のコピーをオフライン保存",
      level: "info",
    });

    if (/中国/.test(destStr)) {
      items.push({
        text: "現地で使う地図・決済・通信手段を事前確認。利用制限があるサービスに注意",
        level: "warn",
      });
    }
  } else {
    items.push({
      text: "天候や交通機関の運休情報を前日・当日に確認",
      level: "info",
    });
  }

  return items;
}

function getLocalItems(destStr, currencyInfos, exchangeRateInfo) {
  const items = [];

  const timezoneMap = [
    { pattern: /韓国|ソウル|釜山|済州/, tz: "時差なし（日本と同じUTC+9）" },
    {
      pattern:
        /台湾|台北|高雄|中国|北京|上海|深圳|香港|マカオ|シンガポール|マレーシア|フィリピン|マニラ/,
      tz: "日本より1時間遅い（UTC+8）",
    },
    {
      pattern:
        /タイ|バンコク|チェンマイ|ベトナム|ハノイ|ホーチミン|ダナン|ラオス|カンボジア/,
      tz: "日本より2時間遅い（UTC+7）",
    },
    {
      pattern: /インド|ムンバイ|デリー/,
      tz: "日本より3時間30分遅い（UTC+5:30）",
    },
    { pattern: /UAE|ドバイ/, tz: "日本より5時間遅い（UTC+4）" },
    {
      pattern:
        /フランス|パリ|ドイツ|ベルリン|イタリア|ローマ|スペイン|マドリード|オランダ|スイス|ポルトガル|オーストリア|ベルギー/,
      tz: "日本より7〜8時間遅い（中央ヨーロッパ、夏時間あり）",
    },
    { pattern: /イギリス|ロンドン/, tz: "日本より9〜8時間遅い（夏時間あり）" },
    {
      pattern: /ニューヨーク|ワシントン|ボストン/,
      tz: "日本より14〜13時間遅い（夏時間あり）",
    },
    {
      pattern: /ロサンゼルス|サンフランシスコ|シアトル|バンクーバー/,
      tz: "日本より17〜16時間遅い（夏時間あり）",
    },
    { pattern: /ハワイ/, tz: "日本より19時間遅い（夏時間なし）" },
    { pattern: /グアム|サイパン/, tz: "日本より1時間進んでいる（UTC+10）" },
    {
      pattern: /オーストラリア|シドニー|メルボルン|ブリスベン/,
      tz: "日本より1〜2時間進んでいる（UTC+10/+11）",
    },
  ];

  for (const entry of timezoneMap) {
    if (entry.pattern.test(destStr)) {
      items.push({ text: `時差: ${entry.tz}`, level: "info" });
      break;
    }
  }

  currencyInfos.forEach((currencyInfo) => {
    const note = currencyInfo.note ? ` — ${currencyInfo.note}` : "";
    items.push({
      text: `通貨: ${currencyInfo.name}（${currencyInfo.code}）${note}`,
      level: "info",
    });
    const exchangeItem = formatExchangeRateItem(currencyInfo, exchangeRateInfo);
    if (exchangeItem) items.push(exchangeItem);
  });

  items.push({
    text: "外務省「海外安全情報」で現地の安全情報を確認",
    level: "info",
  });

  return items;
}

function getOfficialSourceLinks(destType) {
  if (destType !== "international") return [];

  return [
    { label: "外務省 海外安全", href: "https://www.anzen.mofa.go.jp/" },
    { label: "外務省 ビザ", href: "https://www.mofa.go.jp/mofaj/toko/visa/" },
    { label: "FORTH 感染症", href: "https://www.forth.go.jp/index.html" },
    { label: "たびレジ", href: "https://www.ezairyu.mofa.go.jp/tabireg/" },
  ];
}

export default {
  name: "TravelAdvice",
  props: {
    shioriData: { type: Object, default: null },
  },
  setup(props) {
    const destinations = computed(() => {
      const value = props.shioriData?.DESTINATIONS;
      if (Array.isArray(value)) return value;
      try {
        return JSON.parse(value || "[]");
      } catch {
        return [];
      }
    });

    const destinationType = computed(
      () => props.shioriData?.DESTINATION_TYPE || "domestic",
    );

    const destinationText = computed(() => getDestStr(destinations.value));

    const targetCurrencyInfos = computed(() => {
      if (destinationType.value !== "international") return [];
      return getCurrencyInfos(destinations.value);
    });

    const exchangeRateInfo = ref({
      loading: false,
      error: null,
      rates: {},
      missingCodes: [],
      date: null,
    });

    let exchangeRequestId = 0;
    watch(
      () => targetCurrencyInfos.value.map((currency) => currency.code).join(","),
      async (currencyCodes) => {
        exchangeRequestId += 1;
        const requestId = exchangeRequestId;
        if (!currencyCodes) {
          exchangeRateInfo.value = {
            loading: false,
            error: null,
            rates: {},
            missingCodes: [],
            date: null,
          };
          return;
        }

        exchangeRateInfo.value = {
          loading: true,
          error: null,
          rates: {},
          missingCodes: [],
          date: null,
        };

        try {
          const response = await axios.get(`${API_URL}/exchange-rates/latest`, {
            params: { base: "JPY", symbols: currencyCodes },
          });
          if (requestId !== exchangeRequestId) return;
          const requestedCodes = currencyCodes.split(",");
          const rates = requestedCodes.reduce((result, code) => {
            const rate = Number(response.data?.rates?.[code]);
            if (Number.isFinite(rate) && rate > 0) result[code] = rate;
            return result;
          }, {});
          exchangeRateInfo.value = {
            loading: false,
            error: null,
            rates,
            missingCodes: requestedCodes.filter((code) => !rates[code]),
            date: response.data?.date || null,
          };
        } catch (error) {
          if (requestId !== exchangeRequestId) return;
          console.error("為替レート取得エラー:", error);
          exchangeRateInfo.value = {
            loading: false,
            error,
            rates: {},
            missingCodes: currencyCodes.split(","),
            date: null,
          };
        }
      },
      { immediate: true },
    );

    const daysUntilTrip = computed(() => {
      if (!props.shioriData?.TRIP_FROM) return null;
      return daysDiff(props.shioriData.TRIP_FROM);
    });

    const daysUntilTripEnd = computed(() => {
      if (!props.shioriData?.TRIP_TO) return null;
      return daysDiff(props.shioriData.TRIP_TO);
    });

    const tripDurationDays = computed(() => {
      if (!props.shioriData?.TRIP_FROM || !props.shioriData?.TRIP_TO) return 1;
      const from = toDateStr(props.shioriData.TRIP_FROM);
      const to = toDateStr(props.shioriData.TRIP_TO);
      return Math.max(1, Math.round((to - from) / 86400000) + 1);
    });

    const tripMonth = computed(() => {
      if (!props.shioriData?.TRIP_FROM) return new Date().getMonth() + 1;
      return new Date(props.shioriData.TRIP_FROM).getMonth() + 1;
    });

    const tripSeason = computed(() => {
      const m = tripMonth.value;
      return m <= 2 || m === 12
        ? "winter"
        : m <= 5
        ? "spring"
        : m <= 8
        ? "summer"
        : "autumn";
    });

    const countdownInfo = computed(() => {
      const days = daysUntilTrip.value;
      const daysEnd = daysUntilTripEnd.value;
      if (days === null) return null;

      if (daysEnd !== null && daysEnd < 0) {
        return {
          icon: "🏠",
          main: "旅行は終了しました",
          sub: "お疲れ様でした！",
          cls: "cd-past",
        };
      }
      if (days <= 0 && (daysEnd === null || daysEnd >= 0)) {
        return {
          icon: "✈️",
          main: "今日が出発日！",
          sub: "良い旅を！",
          cls: "cd-today",
        };
      }
      if (days === 1) {
        return {
          icon: "🚨",
          main: "明日が出発日！",
          sub: "荷物の最終確認を",
          cls: "cd-urgent",
        };
      }
      if (days <= 3) {
        return {
          icon: "⚡",
          main: `出発まであと${days}日`,
          sub: "最終準備をしましょう",
          cls: "cd-soon",
        };
      }
      if (days <= 7) {
        return {
          icon: "📦",
          main: `出発まであと${days}日`,
          sub: "荷造りを始めましょう",
          cls: "cd-week",
        };
      }
      if (days <= 14) {
        return {
          icon: "📋",
          main: `出発まであと${days}日`,
          sub: "予約・書類の確認を",
          cls: "cd-twoweek",
        };
      }
      if (days <= 30) {
        return {
          icon: "🗓️",
          main: `出発まであと${days}日`,
          sub: "そろそろ準備を始めましょう",
          cls: "cd-month",
        };
      }
      return {
        icon: "🌍",
        main: `出発まであと${days}日`,
        sub: "旅行が楽しみですね",
        cls: "cd-ahead",
      };
    });

    const adviceSections = computed(() => {
      const dests = destinations.value;
      if (!dests || dests.length === 0) return [];

      const destType = destinationType.value;
      const destStr = destinationText.value;
      const month = tripMonth.value;
      const duration = tripDurationDays.value;
      const days = daysUntilTrip.value ?? 999;
      const season = tripSeason.value;
      const sections = [];

      // パスポート・ビザ（国際のみ）
      if (destType === "international") {
        const visaItems = getVisaItems(destStr, days);
        if (visaItems.length > 0) {
          sections.push({
            icon: "🛂",
            title: "パスポート・ビザ",
            items: visaItems,
          });
        }
      }

      // 気候・服装
      const climateItems = getClimateItems(destType, destStr, month);
      if (climateItems.length > 0) {
        sections.push({ icon: "🌡️", title: "気温・服装", items: climateItems });
      }

      // 治安（国際のみ）
      const safetyItems = getSafetyItems(destType, destStr);
      if (safetyItems.length > 0) {
        sections.push({ icon: "🛡️", title: "治安", items: safetyItems });
      }

      // 感染症・健康（国際のみ）
      const healthItems = getHealthItems(destType, destStr);
      if (healthItems.length > 0) {
        sections.push({ icon: "🏥", title: "感染症・健康", items: healthItems });
      }

      // 持ち物チェック
      const packingItems = getPackingItems(
        destType,
        destStr,
        duration,
        days,
        season,
      );
      if (packingItems.length > 0) {
        sections.push({
          icon: "🧳",
          title: "持ち物チェック",
          items: packingItems,
        });
      }

      // 現地情報（国際のみ）
      if (destType === "international") {
        const localItems = getLocalItems(
          destStr,
          targetCurrencyInfos.value,
          exchangeRateInfo.value,
        );
        if (localItems.length > 0) {
          sections.push({ icon: "🌏", title: "現地情報", items: localItems });
        }
      }

      const otherItems = getOtherCautionItems(destType, destStr);
      if (otherItems.length > 0) {
        sections.push({ icon: "⚠️", title: "その他注意事項", items: otherItems });
      }

      return sections;
    });

    const officialSourceLinks = computed(() =>
      getOfficialSourceLinks(destinationType.value),
    );

    return { countdownInfo, adviceSections, officialSourceLinks };
  },
};
</script>

<style scoped>
.travel-advice-wrap {
  margin-top: 1.5rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* ヘッダー */
.advice-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}

.advice-header-icon {
  font-size: 1.125rem;
}

.advice-header-title {
  flex: 1;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.advice-ai-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: #fff;
  padding: 2px 10px;
  border-radius: 99px;
  letter-spacing: 0.02em;
}

/* カウントダウンバナー */
.countdown-banner {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}

.cd-today {
  background: #dcfce7;
}
.cd-urgent {
  background: #fee2e2;
}
.cd-soon {
  background: #fff7ed;
}
.cd-week {
  background: #fffbeb;
}
.cd-twoweek {
  background: #eff6ff;
}
.cd-month {
  background: #f0fdf4;
}
.cd-ahead {
  background: #f9fafb;
}
.cd-past {
  background: #f3f4f6;
}

.countdown-icon {
  font-size: 1.75rem;
  flex-shrink: 0;
}

.countdown-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.countdown-main {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.countdown-sub {
  font-size: 0.8125rem;
  color: #6b7280;
}

/* アドバイス本文 */
.advice-body {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.advice-section-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9375rem;
  color: #374151;
  margin-bottom: 0.5rem;
}

.advice-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.advice-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #374151;
}

.advice-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
}

.advice-item.level-ok .advice-dot {
  background: #22c55e;
}
.advice-item.level-info .advice-dot {
  background: #3b82f6;
}
.advice-item.level-warn .advice-dot {
  background: #f59e0b;
}
.advice-item.level-danger .advice-dot {
  background: #ef4444;
}

.advice-item.level-danger {
  color: #991b1b;
  font-weight: 600;
}
.advice-item.level-warn {
  color: #92400e;
}

.advice-empty {
  font-size: 0.875rem;
  color: #9ca3af;
  text-align: center;
  padding: 1rem 0;
}

.advice-disclaimer {
  font-size: 0.75rem;
  color: #9ca3af;
  padding: 0 1.25rem 0.875rem;
  margin: 0;
}

.official-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  padding: 0 1.25rem 1rem;
}

.official-links-label {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
}

.official-link {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.375rem 0.5rem;
  text-decoration: none;
}

.official-link:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}
</style>
